/**
 * variables.css — CSS Custom Properties
 *
 * PURPOSE:
 *   All theme values used across widget CSS.
 *   Default values are neutral — overridden at runtime
 *   by applyTheme() in init.js using tenant config API.
 *
 * SOURCE OF TRUTH for all --ws-* defaults.
 *   init.js injectSharedStyles() mirrors these values inline.
 *   If you update a value here, update init.js too.
 *
 * NEVER hard-code winery-specific colors anywhere else.
 * ALL color/font references in widget.css use var(--ws-*)
 *
 * ADDING A NEW THEME VARIABLE:
 *   1. Add it here with a neutral default
 *   2. Add it to the theme object in config.js DEFAULT_CONFIG
 *   3. Set it in applyTheme() in init.js
 *   4. Use var(--ws-your-variable) in widget.css
 */

/* ── Web fonts ───────────────────────────────────────────
 * Font FACES are loaded in core/fonts.js, into the host page's <head>.
 *
 * Do NOT add @import or @font-face here: this file is injected into the
 * SHADOW ROOT (init.js injectSharedStyles), and browsers ignore @font-face
 * declared inside a shadow root — the font silently never loads. The
 * relative url()s that used to live here also resolved against the winery's
 * own domain (the CSS is inlined as a string at build time), so they 404'd on
 * every deployed site.
 *
 * Adding a family: add its stylesheet to SHARED_FONT_STYLESHEETS in
 * core/fonts.js, then expose it as a --ws-* var below.
 * ──────────────────────────────────────────────────────── */

:host {
  /* ── Colors ─────────────────────────────────────── */
  --ws-primary: #2c2c2c; /* Main brand color    */
  --ws-accent: #8c8c8c; /* Secondary/accent    */
  --ws-selected-chip: var(--ws-primary); /* Selected guided chip */
  --ws-text: #1a1a1a; /* Body text           */
  --ws-text-light: #ffffff; /* Light text on dark  */
  --ws-background: #ffffff; /* Widget background   */
  --ws-surface: #f5f5f5; /* Cards, panels       */
  --ws-border: #e0e0e0; /* Dividers, borders   */
  --ws-error: #d32f2f; /* Error states        */
  --ws-success: #388e3c; /* Success states      */

  /* ── Typography ─────────────────────────────────── */
  --ws-font: "Open Sans", sans-serif;
  --ws-avenir: "Avenir", sans-serif;
  --ws-engraver: "EngraversRomanBTS", var(--ws-font);
  --ws-lora: "Lora";
  --ws-lato: "Lato";
  --ws-monst: "Montserrat", sans-serif;
  --ws-karla: "Karla", sans-serif;
  --ws-lithos: "LithosPro";
  --ws-font-size: 14px;
  --ws-line-height: 1.5;

  /* Applied, not just declared: the shadow root inherits font-family from the
     host page unless we set it here. Without this the winery's own body font
     (e.g. Courier Prime on a Webflow site) leaks into every widget element
     that uses font-family: inherit.

     font-size/line-height are pinned to their CSS INITIAL values (medium =
     16px, normal), not to --ws-font-size/--ws-line-height. That is deliberate:
     `medium`/`normal` block the host page from cascading in without imposing a
     new value, so the widget renders byte-identically to how it always has on a
     normal host page while becoming immune to a winery that sets an unusual
     body font-size or line-height. Setting 14px/1.5 here instead WOULD resize
     and respace every element — don't. */
  font-family: var(--ws-font);
  font-size: medium;
  line-height: normal;

  /* ── Layout ─────────────────────────────────────── */
  --ws-border-radius: 12px;
  --ws-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  --ws-widget-width: 380px;
  --ws-widget-height: 600px;

  /* ── Chat bubbles & rows ─────────────────────────── */
  --ws-bot-bubble-bg: #f0f0f0; /* Bot message background    */
  --ws-bot-bubble-color: var(--ws-text);
  --ws-user-bubble-bg: var(--ws-primary);
  --ws-user-bubble-bg-gradient: linear-gradient(#989696, #807c7c);
  --ws-user-bubble-color: #ffffff;
  --ws-bubble-radius: 12px; /* Outer corners             */
  --ws-bubble-tail-radius: 2px; /* Corner pointing to avatar */
  --ws-avatar-size: 24px;
  --ws-avatar-gap: 8px; /* Avatar ↔ bubble gap       */
  --ws-avatar-bg: transparent;
  --ws-message-gap: 8px; /* Vertical gap between rows */

  /* ── Custom scrollbar ────────────────────────────── */
  --ws-scrollbar-width: 24px;
  --ws-scrollbar-bg: #fcfcfc;
  --ws-scrollbar-border: #e8e8e8;
  --ws-scrollbar-btn-bg: #d8d3d1;
  --ws-scrollbar-thumb-bg: #d8d3d1;
  --ws-scrollbar-icon-color: #574d39;
  --ws-scrollbar-thumb-min: 50px;
  --ws-scrollbar-btn-jump-h: 40px;
  --ws-scrollbar-btn-step-h: 30px;

  /* ── Mic button ──────────────────────────────────── */
  --ws-mic-bg: #e8e8e8; /* Idle background     */
  --ws-mic-color: #574d39; /* Idle icon color     */
  --ws-mic-active-bg: var(--ws-primary);
  --ws-mic-active-color: #ffffff;

  /* ── Header action buttons & text ──────────────────── */
  --ws-header-action-color: #ffffff; /* Refresh / minimize / close icon color   */
  --ws-header-logo-color: #ffffff; /* Bot-name text when no header_icon_url   */
  --ws-header-logo-height: 32px; /* header_icon_url logo height; per-winery via header_logo_height */

  /* ── Feature cards ───────────────────────────────── */
  --ws-card-accent: var(--ws-primary); /* Card title + CTA accent */
  --ws-card-title-font: var(--ws-font); /* Card title typeface     */

  /* ── Rich winery cards (cards.css) ───────────────────
     Shared structure lives in cards.css; these are the NEUTRAL defaults
     (currently the halleck/magenta palette). A variant themes its cards by
     overriding only the vars it needs in its own styles.css. */
  --ws-rich-card-bg: #ffffff;
  --ws-rich-card-border: #e5e0d7;
  --ws-rich-card-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  --ws-rich-deep: #691b38; /* deep burgundy — soldout, membership, reserve */
  --ws-rich-accent: #972553; /* wine-magenta — tasting/check/event accents    */
  --ws-rich-accent-hover: #a93363;
  --ws-rich-on-accent: #ffffff; /* text on deep/accent fills                      */
  --ws-rich-title: #1a1a1a;
  --ws-rich-muted: #574d39; /* unit / bullet text                             */
  --ws-rich-tasting-border: #c9a2b4;
  --ws-rich-inner-from: #fbf1f5; /* membership inner gradient top                  */
  --ws-rich-review-bg: #ffffff;
  --ws-rich-review-border: #a29b8d;
  --ws-rich-review-fg: #574d39;
  --ws-rich-review-hover-bg: #fbf1f5;
  --ws-rich-event-overlay: rgba(45, 20, 35, 0.5);
  --ws-rich-cta-bg: #972553;
  --ws-rich-cta-bg-hover: #a93363;
  --ws-rich-prompt-from: #972553;
  --ws-rich-prompt-to: #691b38;
  --ws-rich-prompt-hover-from: #a93363;

  /* ── Wine drawer (tasting notes / food pairings) ───── */
  --ws-drawer-heading: var(--ws-rich-deep);
  --ws-drawer-tab-active: var(--ws-rich-accent);
  --ws-drawer-tab-active-text: var(--ws-rich-on-accent);
  --ws-drawer-cta: var(--ws-rich-cta-bg);
  --ws-drawer-cta-text: #ffffff;
  --ws-drawer-surface: var(--ws-rich-inner-from, #fdf5f7);
  --ws-drawer-card-accent: var(--ws-rich-deep);
  --ws-drawer-muted: #737373;
  --ws-drawer-bottom: 90px;
  /* ── Animation ───────────────────────────────────── */
  --ws-transition: 0.2s ease;
}

/* ════════════════════════════════════════════════════════════════════════
   Shared rich-card component styles moved to src/styles/cards.css (var-driven).
   Per-winery overrides live in src/variants/<slug>/styles.css. init.js injects:
   variables.css (vars) → cards.css (structure) → variant CSS (vars + overrides).
   ════════════════════════════════════════════════════════════════════════ */
